<template>
  <div class="color-match flex flex-col h-full w-full items-center justify-center">
    <div class="grid gap-1.5 rounded-2xl p-2 bg-[#1c4848]/10 border border-[#1c4848]/10" :style="gridStyle">
      <button
        v-for="(cell, idx) in grid"
        :key="idx"
        type="button"
        class="bubble aspect-square rounded-full border-2 border-white/20 shadow-md transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#ecd79f]"
        :class="!cell ? 'bg-[#1c4848]/5' : selectedIndex === idx ? 'ring-2 ring-[#ecd79f] ring-offset-2' : ''"
        :style="cell ? (cell.special ? { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' } : { backgroundColor: COLORS[cell.color % COLORS.length] }) : {}"
        :disabled="!cell || isAnimating"
        @click="onCellClick(idx)"
      >
        <span v-if="cell && cell.special === 'rainbow'" class="text-white text-[10px] font-bold">★</span>
      </button>
    </div>
    <p class="mt-4 text-sm text-[#1c4848]/60">Swap two adjacent bubbles to match 3+ in a row or column.</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { parseBubbleConfig } from './parseGameConfig.js';

const COLORS = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#0097a7', '#fdd835', '#5d4037'];

const props = defineProps({ game: { type: Object, required: true } });
const emit = defineEmits(['win', 'stats']);

const config = computed(() => parseBubbleConfig(props.game?.config));
const cols = computed(() => config.value.gridCols);
const rows = computed(() => 10);
const grid = ref([]);
const score = ref(0);
const selectedIndex = ref(null);
const isAnimating = ref(false);

const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${cols.value}, minmax(0, 1fr))`,
  width: Math.min(360, cols.value * 44) + 'px',
}));

function getColor() {
  const n = config.value.colorsCount;
  return Math.floor(Math.random() * n);
}

function maybeSpecial() {
  return Math.random() < config.value.specialChance ? { special: 'rainbow', color: 0 } : { color: getColor() };
}

function indexToRowCol(i) {
  const c = cols.value;
  return { r: Math.floor(i / c), col: i % c };
}

function rowColToIndex(r, col) {
  return r * cols.value + col;
}

function isAdjacent(i, j) {
  const a = indexToRowCol(i);
  const b = indexToRowCol(j);
  return (Math.abs(a.r - b.r) === 1 && a.col === b.col) || (a.r === b.r && Math.abs(a.col - b.col) === 1);
}

function getMatches(arr) {
  const c = cols.value;
  const minM = config.value.minMatch;
  const set = new Set();
  const key = (i) => (arr[i]?.color ?? -1);
  const addRow = (r) => {
    let start = 0;
    for (let col = 1; col <= c; col++) {
      if (col === c || key(r * c + col) !== key(r * c + start)) {
        if (col - start >= minM) for (let x = start; x < col; x++) set.add(r * c + x);
        start = col;
      }
    }
  };
  const addCol = (col) => {
    let start = 0;
    for (let r = 1; r <= rows.value; r++) {
      const idx = r * c + col;
      const prev = (r - 1) * c + col;
      if (r === rows.value || key(idx) !== key(prev)) {
        if (r - start >= minM) for (let x = start; x < r; x++) set.add(x * c + col);
        start = r;
      }
    }
  };
  for (let r = 0; r < rows.value; r++) addRow(r);
  for (let col = 0; col < c; col++) addCol(col);
  return [...set];
}

function removeAndDrop(arr) {
  const c = cols.value;
  const next = [...arr];
  const toRemove = new Set(getMatches(next));
  toRemove.forEach((i) => { next[i] = null; });
  for (let col = 0; col < c; col++) {
    const column = [];
    for (let r = 0; r < rows.value; r++) {
      const idx = r * c + col;
      if (next[idx]) column.push(next[idx]);
    }
    const numNulls = rows.value - column.length;
    for (let r = 0; r < rows.value; r++) next[r * c + col] = r < numNulls ? null : column[r - numNulls];
  }
  return { grid: next, removed: toRemove.size };
}

function fillEmpty(arr) {
  const c = cols.value;
  const next = arr.map((cell) => (cell ? { ...cell } : maybeSpecial()));
  return next;
}

function onCellClick(idx) {
  if (isAnimating.value || !grid.value[idx]) return;
  if (selectedIndex.value === null) {
    selectedIndex.value = idx;
    return;
  }
  if (selectedIndex.value === idx) {
    selectedIndex.value = null;
    return;
  }
  if (!isAdjacent(selectedIndex.value, idx)) {
    selectedIndex.value = idx;
    return;
  }
  const arr = grid.value.map((c) => c ? { ...c } : null);
  [arr[selectedIndex.value], arr[idx]] = [arr[idx], arr[selectedIndex.value]];
  const { grid: after, removed } = removeAndDrop(arr);
  if (removed === 0) {
    selectedIndex.value = null;
    return;
  }
  score.value += removed * 10;
  grid.value = fillEmpty(after);
  selectedIndex.value = null;
  emit('stats', { score: score.value });
}

function init() {
  const c = cols.value;
  const r = rows.value;
  const initRows = Math.min(config.value.initialRows, r);
  const total = c * r;
  const arr = Array(total).fill(null);
  for (let i = 0; i < total; i++) {
    const row = Math.floor(i / c);
    if (row >= r - initRows) arr[i] = maybeSpecial();
  }
  grid.value = fillEmpty(arr);
  score.value = 0;
  selectedIndex.value = null;
  emit('stats', { score: 0 });
}

watch(() => [props.game?.id, config.value.gridCols, config.value.initialRows], () => {
  if (props.game?.id) init();
}, { immediate: true });

defineExpose({ restart: init });
</script>

<style scoped>
.bubble { background-color: var(--bubble-color, #78909c); }
</style>
